import { DateTime } from 'luxon';

import config from './config';

const isStorageAvailable = (storage: unknown): storage is Storage => {
    return (
        typeof storage !== 'undefined' &&
        storage !== null &&
        typeof (storage as Storage).getItem === 'function' &&
        typeof (storage as Storage).setItem === 'function' &&
        typeof (storage as Storage).removeItem === 'function'
    );
};

// Safely resolve a Web Storage global. Returns null if the global is
// undefined (SSR), explicitly null (some webviews / sandboxed iframes),
// or throws on access (Safari with cookies disabled raises SecurityError
// when reading window.sessionStorage / window.localStorage).
const safeGetStorage = (kind: 'session' | 'local'): Storage | null => {
    try {
        const storage = kind === 'session' ? sessionStorage : localStorage;
        return isStorageAvailable(storage) ? storage : null;
    } catch {
        return null;
    }
};

const getBrowserStorages = (): { localStorage: Storage | null; sessionStorage: Storage | null } => {
    return {
        localStorage: safeGetStorage('local'),
        sessionStorage: safeGetStorage('session'),
    };
};

export const getSessionStorageItem = (key: string): string | null => {
    const storage = safeGetStorage('session');
    if (!storage) return null;
    try {
        return storage.getItem(key);
    } catch {
        return null;
    }
};

export const setSessionStorageItem = (key: string, value: string): void => {
    const storage = safeGetStorage('session');
    if (!storage) return;
    try {
        storage.setItem(key, value);
    } catch {
        // Quota exceeded or storage disabled mid-session; nothing actionable.
    }
};

export const removeSessionStorageItem = (key: string): void => {
    const storage = safeGetStorage('session');
    if (!storage) return;
    try {
        storage.removeItem(key);
    } catch {
        // Storage disabled mid-session; nothing actionable.
    }
};

export const getLocalStorageItem = (key: string): string | null => {
    const storage = safeGetStorage('local');
    if (!storage) return null;
    try {
        return storage.getItem(key);
    } catch {
        return null;
    }
};

export const setLocalStorageItem = (key: string, value: string): void => {
    const storage = safeGetStorage('local');
    if (!storage) return;
    try {
        storage.setItem(key, value);
    } catch {
        // Quota exceeded or storage disabled mid-session; nothing actionable.
    }
};

const setWithExpiration = (key: string, value: any, isLocalStorage: boolean, length: number): void => {
    // `item` is an object which contains the original value
    // as well as the time when it's supposed to expire
    const item = JSON.stringify({
        value: value,
        expiry: DateTime.now().toSeconds() + length,
    });

    const storages = getBrowserStorages();
    if (!isStorageAvailable(storages.localStorage) || !isStorageAvailable(storages.sessionStorage)) {
        return;
    }

    try {
        if (isLocalStorage) {
            storages.localStorage.setItem(key, item);
        } else {
            storages.sessionStorage.setItem(key, item);
        }
    } catch {
        // Quota exceeded or storage disabled mid-session; nothing actionable.
    }
};

const setLocalStorageWithExpiration = (key: string, value: any, length = config.sessionLength): void => {
    setWithExpiration(key, value, true, length);
};

const setSessionStorageWithExpiration = (key: string, value: any, length = config.sessionLength): void => {
    setWithExpiration(key, value, false, length);
};

const getWithExpiration = (key: string, isLocalStorage: boolean): any => {
    const storages = getBrowserStorages();
    if (!isStorageAvailable(storages.localStorage) || !isStorageAvailable(storages.sessionStorage)) {
        return null;
    }

    const itemStr = isLocalStorage ? storages.localStorage.getItem(key) : storages.sessionStorage.getItem(key);

    // if the item doesn't exist, return null
    if (!!itemStr) {
        try {
            const item = JSON.parse(itemStr);
            // compare the expiry time of the item with the current time
            if (DateTime.now().toSeconds() > item.expiry) {
                // If the item is expired, delete the item from storage
                // and return null
                if (isLocalStorage) {
                    storages.localStorage.removeItem(key);
                } else {
                    storages.sessionStorage.removeItem(key);
                }
                return null;
            }
            return item.value;
        } catch {
            return null;
        }
    }

    return null;
};

const getLocalStorageWithExpiration = (key: string): any => {
    return getWithExpiration(key, true);
};

const getSessionStorageWithExpiration = (key: string): any => {
    return getWithExpiration(key, false);
};

const useLocalCookies = process.env.NEXT_PUBLIC_USE_LOCAL_COOKIES === 'true';

/**
 * Returns the `; domain=…` fragment for cookie operations. On production /
 * staging the cookie is scoped to .kalshi.com / .kalshi.co. When
 * NEXT_PUBLIC_USE_LOCAL_COOKIES is set, returns empty string so the cookie
 * is readable on localhost and Vercel preview deployments.
 */
const getCookieDomain = (): string =>
    useLocalCookies ? '' : `; domain=${config.frontendEnv === 'production' ? '.kalshi.com' : '.kalshi.co'}`;

const setCookieWithExpiration = (key: string, value: string, length = config.sessionLength): void => {
    if (typeof document === 'undefined') {
        return;
    }

    const expiryDate = new Date();
    expiryDate.setTime(expiryDate.getTime() + length * 1000); // Convert seconds to milliseconds

    document.cookie = `${key}=${encodeURIComponent(value)}; expires=${expiryDate.toUTCString()}; path=/${getCookieDomain()}; SameSite=Lax`;
};

/**
 * Removes a cookie scoped to .kalshi.com domain.
 * Also clears the legacy (domainless) cookie to handle cookies set before domain-scoping was added.
 * @param key Cookie name
 */
const removeCookie = (key: string): void => {
    if (typeof document === 'undefined') {
        return;
    }

    const domain = config.frontendEnv === 'production' ? '.kalshi.com' : '.kalshi.co';

    // Clear domain-scoped cookie
    document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}; SameSite=Lax`;
    // Clear legacy cookie set without domain attribute (won't match the domain-scoped delete above)
    document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax`;
};

/**
 * Gets a cookie value by key
 * @param key Cookie name
 * @returns Cookie value or null if not found
 */
const getCookie = (key: string): string | null => {
    if (typeof document === 'undefined') {
        return null;
    }
    const cookies = document.cookie.split('; ');
    const cookie = cookies.find((c) => c.startsWith(`${key}=`));
    if (cookie) {
        return decodeURIComponent(cookie.split('=')[1]);
    }
    return null;
};

const removeLocalStorageItem = (key: string): void => {
    const storages = getBrowserStorages();
    if (!isStorageAvailable(storages.localStorage)) {
        return;
    }
    storages.localStorage.removeItem(key);
};

export {
    getLocalStorageWithExpiration,
    setLocalStorageWithExpiration,
    getSessionStorageWithExpiration,
    setSessionStorageWithExpiration,
    setCookieWithExpiration,
    removeCookie,
    getCookie,
    removeLocalStorageItem,
};
